import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Tooltip,
    Paper,
} from '@mui/material';
import { useDrop } from 'react-dnd';
import ProductivityChat from '../../components/ProductivityChatWindows/FBGProductivityChat';
import TaskButton from '../../components/TaskButtons/TaskButton';
import figAgentLogo from '../../assets/fig_agent.png';
import figLogo from '../../assets/fig_CROP.png';

const ItemType = 'task';

const Sandbox = () => {
    const [droppedTasks, setDroppedTasks] = useState([]);
    const [, drop] = useDrop({
        accept: ItemType,
        drop: (draggedItem) => {
            const newId = draggedItem.id || `task-${Date.now()}`;
            setDroppedTasks((prev) => [...prev, { id: newId, content: draggedItem.content }]);
        },
    });

    const handleDeleteTask = (taskId) => {
        setDroppedTasks((prev) => prev.filter((t) => t.id !== taskId));
    };

    const openRequestForm = () => {
        window.open(
            'https://app.smartsheet.com/b/form/3507048c22ba47c196575bfc27d8407a',
            '_blank',
            'width=800,height=800,top=100,left=100'
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f9f9f9' }}>
            {/* Instructions Section */}
            <Box
                sx={{
                    backgroundColor: '#eef4f7',
                    p: 3,
                    borderBottom: '2px solid #ccc',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 2,
                }}
            >
                {/* Column 1: Title, description, ask list, button */}
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                        USDA Food Buying Guide Assistant
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#555', mb: 1 }}>
                        This chat agent is trained specifically on the USDA Food Buying Guide (FBG)—the federal reference for purchasing and planning nutritious, cost‑effective meals in schools, childcare, and other child‑nutrition programs.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
                        Ask for:
                        <ul style={{ margin: '4px 0 0 16px', padding: 0, textAlign: 'left' }}>
                            <li>Serving yields, purchase quantities, & conversion factors</li>
                            <li>Crediting information for meal‑pattern compliance</li>
                            <li>Recipe scaling and cost projections</li>
                            <li>Suggested substitutions or product specs</li>
                            <li>Any other FBG‑related guidance</li>
                        </ul>
                    </Typography>
                    <Button variant="outlined" onClick={openRequestForm} sx={{ mt: 1 }}>
                        Submit a Request Form
                    </Button>
                </Box>

                {/* Column 2: Example Request Card */}
                <Box>
                    <Paper
                        elevation={1}
                        sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1, height: '100%' }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#222' }}>
                            Example Request:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#333' }}>
                            {`I'm planning a school‑lunch entrée for 200 K‑8 students that meets the NSLP meal pattern for a 2‑ounce‑eq meat/meat‑alternate.
• Recommend a chicken‑based recipe drawn from the Food Buying Guide (FBG).
• For each ingredient, list: purchase quantity (AP), servings/yield (EP), crediting information, and the FBG reference (section & item).
• Show a cost projection using current FBG price estimates.
• Identify at least one vegetarian substitution that would provide the same crediting.
• Explain any conversion factors you used.`}
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {/* Main Content (Chat + Drop Area) */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Left: Chat */}
                <Box sx={{ width: '35%', p: 3, backgroundColor: '#eaeaea', borderRight: '2px solid #ccc', overflowY: 'auto' }}>
                    <ProductivityChat />
                </Box>

                {/* Right: Drop Zone */}
                <Box ref={drop} sx={{ flex: 1, p: 3, backgroundColor: '#fff', overflowY: 'auto', position: 'relative' }}>
                    {/* Watermark Logos */}
                    <Box
                        component="img"
                        src={figAgentLogo}
                        alt="FIG Agent Logo Watermark"
                        sx={{
                            position: 'absolute',
                            width: 400,
                            height: 400,
                            opacity: 0.08,
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        component="img"
                        src={figLogo}
                        alt="FIG Logo"
                        sx={{ position: 'absolute', width: 300, height: 100, opacity: 0.04, bottom: 16, right: 16, pointerEvents: 'none' }}
                    />

                    {/* Dropped Tasks */}
                    {droppedTasks.map((task) => (
                        <Box
                            key={task.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                p: 1,
                                mb: 1,
                                backgroundColor: '#f9f9f9',
                            }}
                        >
                            <TaskButton content={task.content} />
                            <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteTask(task.id)}>
                                Delete
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default Sandbox;
